import { Reveal, Stagger, StaggerItem, SectionNo, Hairline, MaskReveal, PlaceholderTag } from "@/components/primitives";
import MediaImage from "@/components/MediaImage";
import AmbientBand from "@/components/AmbientBand";
import BigSectionNo from "@/components/BigSectionNo";
import Counter from "@/components/Counter";
import { placeholders } from "@/config/site";

const SPEC = [
  { item: "Finished films", value: `${placeholders.heroFilmDuration} each`, ph: true },
  { item: "16:9 landscape masters", value: "web & booking channels" },
  { item: "9:16 vertical cuts", value: "Reels · Stories · TikTok" },
  { item: "1:1 square", value: "where the channel needs it" },
  { item: "Short-form cutdowns", value: `${placeholders.cutdownDuration}, for paid`, ph: true },
  { item: "Stills from finished frames", value: "full resolution" },
  { item: "Masters", value: "ProRes + web-optimised MP4" },
  { item: "Licence, stated plainly", value: placeholders.licenceTerms, ph: true },
  { item: "Turnaround", value: placeholders.turnaroundDays, ph: true },
];

export default function Deliverables() {
  return (
    <section id="deliverables" className="relative overflow-hidden px-6 py-20 sm:px-10 md:py-28 warm-radial-r" data-testid="deliverables">
      <AmbientBand loop="L02" texture="T02" opacity={0.07} />
      <BigSectionNo n="03" />
      <div className="relative z-10 mx-auto max-w-[1280px]">
        <Hairline className="mb-20" />
        <Reveal>
          <SectionNo n="03" label="What you receive" />
        </Reveal>

        <div className="mt-12 grid gap-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-24">
          <div>
            <h2
              className="display text-primaryText"
              style={{
                fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                lineHeight: 1.03,
                letterSpacing: "-0.015em",
                fontWeight: 300,
              }}
            >
              <MaskReveal>Exactly what lands</MaskReveal>
              <MaskReveal delay={0.1}>in your inbox.</MaskReveal>
            </h2>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-mutedText sm:text-lg">
                No mood films that can't be posted, no missing formats on
                delivery day. The complete set, named for its channel, ready to
                upload, with the licence terms written where you can read them.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 text-base italic text-mutedText">
                Delivered publish-ready. Nothing to edit, nothing to reformat.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 grid max-w-md grid-cols-3 gap-6" data-testid="deliverables-counts">
                <div>
                  <div className="display text-4xl text-gold sm:text-5xl">
                    <Counter to={7} />
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-mutedText">FORMATS</div>
                </div>
                <div>
                  <div className="display text-4xl text-gold sm:text-5xl">
                    <Counter to={24} suffix=" fps" />
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-mutedText">CADENCE</div>
                </div>
                <div>
                  <div className="display text-4xl text-gold sm:text-5xl">
                    <Counter to={0} suffix=" crew" />
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-mutedText">ON SITE</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-10 grid grid-cols-4 gap-3" data-testid="deliverables-illustrations">
                {[
                  { id: "S02", label: "ROOMS" },
                  { id: "S15", label: "DINING" },
                  { id: "S09", label: "DETAIL" },
                  { id: "S20", label: "SPA" },
                ].map((i) => (
                  <figure key={i.id} className="group">
                    <div className="aspect-[4/5] overflow-hidden rounded-[2px] border border-hairline transition-colors duration-500 group-hover:border-gold/50">
                      <MediaImage
                        id={i.id}
                        sizes="12vw"
                        className="atmos h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                      />
                    </div>
                    <figcaption className="mt-2 font-mono text-[9px] tracking-[0.2em] text-mutedText">
                      {i.label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.08}>
            <div
              className="rounded-[3px] border border-hairline bg-surface/70 p-8 shadow-[0_20px_60px_-30px_rgba(50,40,20,0.35)] backdrop-blur-[2px] sm:p-10"
              data-testid="spec-block"
            >
              <div className="mb-8 flex items-center justify-between border-b border-hairline pb-4">
                <span className="font-mono text-[11px] tracking-[0.22em] text-gold">
                  SPECIFICATION
                </span>
                <span className="font-mono text-[11px] tracking-[0.22em] text-mutedText">
                  PER PROJECT
                </span>
              </div>
              <ul>
                {SPEC.map((s) => (
                  <li
                    key={s.item}
                    className="group flex items-baseline justify-between gap-6 border-b border-hairline py-5 transition-colors duration-300 last:border-b-0 hover:border-gold/40"
                  >
                    <span
                      className="display text-primaryText transition-colors duration-300 group-hover:text-gold"
                      style={{ fontSize: "clamp(1rem, 1.2vw, 1.125rem)", fontWeight: 300, letterSpacing: "-0.005em" }}
                    >
                      {s.item}
                    </span>
                    <span className="shrink-0 text-right font-mono text-[11px] tracking-[0.14em] text-mutedText">
                      {s.value}
                      {s.ph && <PlaceholderTag />}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
